import { css } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { SlackButtonWithAccountLinking } from '@coursera-web/slack-account-link';
import type { SlackAccount } from '@coursera-web/slack-account-link';
import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Typography, Typography2 } from '@coursera/cds-core';

import { Card } from 'bundles/program-loggedin-shared/components/Card';

import _t from 'i18n!nls/degree-home';

type InputProps = {
  degreeId: string;
};

type Props = InputProps & {
  teamName?: string;
  teamDomain?: string;
  teamMembersCount?: number;
  account?: SlackAccount;
  inProgress?: boolean;
  refetch?: () => void;
};

export const SlackAccountInfoCard = ({
  teamMembersCount = 0,
  account,
  teamDomain,
  degreeId,
  inProgress,
  refetch,
}: Props) => {
  const classMatesWithSlackAccountCount = teamMembersCount > 0 ? teamMembersCount - 1 : 0;
  const slackLink = teamDomain ? `https://${teamDomain}.slack.com` : 'https://slack.com/get-started';

  return account ? (
    <Card className="rc-SlackAccountInfoCard">
      <Typography component="h2" variant="h2semibold">
        {_t('Talk to Classmates')}
      </Typography>
      <Typography2
        component="p"
        variant="bodyPrimary"
        css={css`
          padding-bottom: 12px;
          padding-top: 12px;
        `}
      >
        {classMatesWithSlackAccountCount >= 10 ? (
          <FormattedHTMLMessage
            message={_t('<strong>{number}</strong> of your classmates are on Slack.')}
            number={classMatesWithSlackAccountCount}
          />
        ) : (
          <span>{_t('Your classmates are talking on Slack. Join them!')}</span>
        )}
      </Typography2>

      <SlackButtonWithAccountLinking
        accountStatus={account.status}
        degreeId={degreeId}
        email=""
        slackLink={account.status === 'EMAIL_VERIFIED' ? slackLink : ''}
        className={
          account.status === 'EMAIL_VERIFIED'
            ? 'slack-account-linked-button slack-account-info-card-button'
            : 'slack-account-unlinked-button slack-account-info-card-button'
        }
        buttonText="Slack"
        classMatesWithSlackAccountCount={classMatesWithSlackAccountCount}
        onUpdate={() => {
          if (refetch) {
            refetch();
          }
        }}
        teamDomain={teamDomain}
        disabled={!!inProgress}
        cdsEnabled={true}
      />
    </Card>
  ) : null;
};

export default compose<Props, InputProps>(
  withProps((props: InputProps) => ({ degreeId: props.degreeId })),
  /* eslint-disable graphql/template-strings */
  graphql(
    gql`
      query SlackTeamInformationQuery($degreeId: String) {
        ExternalSlackTeamInformationV1Resource(degreeId: $degreeId)
          @rest(
            type: "ExternalSlackTeamInformationV1Resource"
            path: "externalSlackTeamInformation.v1?q=byDegree&degreeId={args.degreeId}&fields=slackTeam,slackTeamMembersCount"
            method: "GET"
          ) {
          elements @type(name: "ExternalSlackTeamInformation") {
            id
            slackTeam
            slackTeamMembersCount
          }
        }
      }
    `,
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props: ({ data: { ExternalSlackTeamInformationV1Resource } }: any) => ({
        teamName: ExternalSlackTeamInformationV1Resource?.elements[0]?.slackTeam?.slackTeamName,
        teamDomain: ExternalSlackTeamInformationV1Resource?.elements[0]?.slackTeam?.slackTeamDomain,
        teamMembersCount: ExternalSlackTeamInformationV1Resource?.elements[0]?.slackTeamMembersCount,
      }),
    }
  ),
  /* eslint-disable graphql/template-strings */
  graphql(
    gql`
      query SlackMyAccountV2Query($degreeId: String) {
        SlackAuthV1Resource(degreeId: $degreeId)
          @rest(
            type: "SlackAuthV1Resource"
            path: "slackAuth.v1?q=me&degreeId={args.degreeId}&fields=email,status"
            method: "GET"
          ) {
          elements @type(name: "SlackAccount") {
            id
            email
            status
            degreeId
          }
        }
      }
    `,
    {
      // Fetch this data only if we have a teamDomain associated with this degreeId
      // @ts-expect-error react-apollo@2.2.0
      skip: ({ teamDomain }) => !teamDomain,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props: ({ data: { refetch, loading, SlackAuthV1Resource } }: any) => ({
        account: SlackAuthV1Resource?.elements[0] || null,
        inProgress: loading,
        refetch,
      }),
    }
  )
)(SlackAccountInfoCard);
